import React from 'react';
import { useParams } from 'react-router-dom';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';

import Loading from '../../../style-components/Loading.jsx';
import Breadcrumb from '../../Custom-UI/Breadcrumb.jsx';
import ResourceThumb from '../components/ResourceThumb.jsx';

import { useGetLearningPath } from '../../../models/learning_path.js';
import { useGetCourse } from '../../../models/course.js';
import { RESOURCE_TYPES } from '../constants.js';
import { buildResourceUrl, teacherName, STATS } from './utils.js';

// Íconos de las tarjetas de estadísticas, indexados por la key de STATS.
const STAT_ICONS = {
  units: SchoolOutlinedIcon,
  images: ImageOutlinedIcon,
  videos: PlayCircleOutlineIcon,
  anatomical_structures: ScienceOutlinedIcon,
};

// Ver si se puede usar otra funcion ya creada (por ahora se mantendrá)
const formatDate = value => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

function LearningPathView() {
  const { id: courseId, learning_path_id: learningPathId } = useParams();

  const [path, loading] = useGetLearningPath(courseId, learningPathId);
  const [course] = useGetCourse(courseId);

  // Abre el detalle del recurso en una pestaña nueva (ruta /resources/...).
  const openResource = (type, item) => {
    const url = buildResourceUrl(type.key, item.path);
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const breadcrumbItems = [
    { name: 'Cursos', href: '/courses' },
    { name: course?.name ?? 'Curso', href: '/courses' },
    { name: 'Rutas de Aprendizaje', href: `/courses/${courseId}/learning-paths` },
    { name: path?.title ?? 'Ruta', href: '' },
  ];

  if (loading) {
    return (
      <div className='flex w-full justify-center py-20'>
        <Loading />
      </div>
    );
  }

  if (!path) {
    return (
      <div className='w-full p-5'>
        <div className='rounded-xl border border-border bg-white px-6 py-16 text-center text-muted-foreground'>
          No se encontró la ruta de aprendizaje.
        </div>
      </div>
    );
  }

  return (
    <div className='antialiased font-sans flex w-full flex-col gap-5 p-5'>
      {/* Breadcrumb */}
      <div className='rounded-xl border border-border bg-white px-6 py-4'>
        <Breadcrumb items={breadcrumbItems} maxItems={4} />
      </div>

      {/* Hero */}
      <div className='rounded-xl bg-gradient-to-br from-primary to-blue-950 px-8 py-7 text-white shadow-solid-5'>
        <div className='flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-white/80'>
          <span className='flex h-8 w-8 items-center justify-center rounded-lg bg-white/15'>
            <MapOutlinedIcon fontSize='small' />
          </span>
          Ruta de Aprendizaje
        </div>
        <h1 className='mt-4 text-3xl font-bold leading-tight text-left'>{path.title}</h1>
        {path.description && (
          <p className='mt-3 max-w-max text-white/90 text-justify'>{path.description}</p>
        )}
        <div className='mt-5 flex flex-wrap items-center gap-x-8 gap-y-2 border-t border-white/20 pt-4 text-sm text-white/90'>
          <span className='flex items-center gap-2'>
            <PersonOutlineIcon fontSize='small' />
            {teacherName(path.teacher)}
          </span>
          <span className='flex items-center gap-2'>
            <CalendarTodayOutlinedIcon fontSize='small' />
            {formatDate(path.created_at)}
          </span>
          <span className='flex items-center gap-2'>
            <MenuBookOutlinedIcon fontSize='small' />
            {course?.name ?? 'Curso'}
          </span>
        </div>
      </div>

      <div className='grid grid-cols-2 divide-border rounded-xl border border-border bg-white md:grid-cols-4 md:divide-x'>
        {STATS.map(stat => {
          const Icon = STAT_ICONS[stat.key];
          const value = path.stats?.[stat.key] ?? 0;
          return (
            <div key={stat.key} className='flex items-center gap-4 px-6 py-5'>
              <span
                className={`flex h-12 w-12 items-center justify-center rounded-xl ${stat.tile}`}>
                {Icon && <Icon />}
              </span>
              <span className='flex flex-col'>
                <span className={`text-2xl font-bold leading-none ${stat.valueColor}`}>
                  {value}
                </span>
                <span className='mt-1 text-sm text-muted-foreground'>{stat.label}</span>
              </span>
            </div>
          );
        })}
      </div>

      {(path.units || []).map((unit, index) => {
        const unitTotal =
          unit.images.length + unit.videos.length + unit.anatomical_structures.length;
        return (
          <div key={unit.learnunit_id} className='rounded-xl border border-border bg-white'>
            <div className='flex items-center justify-between px-6 py-5'>
              <div className='flex items-center gap-4'>
                <span className='flex h-11 w-11 items-center justify-center rounded-xl bg-primary text-lg font-bold text-white'>
                  {index + 1}
                </span>
                <div className='flex flex-col'>
                  <span className='text-xs font-semibold uppercase tracking-wider text-muted-foreground'>
                    Unidad {index + 1}
                  </span>
                  <span className='text-lg font-bold text-primary'>{unit.name}</span>
                </div>
              </div>
              <span className='rounded-full bg-muted px-3 py-1 text-sm font-medium text-muted-foreground'>
                {unitTotal} {unitTotal === 1 ? 'recurso' : 'recursos'}
              </span>
            </div>

            <div className='grid gap-6 border-t border-border px-6 py-6 md:grid-cols-3'>
              {RESOURCE_TYPES.map(type => {
                const items = unit[type.key];
                const Icon = type.Icon;
                return (
                  <div key={type.key}>
                    <div className={`mb-3 flex items-center gap-2 ${type.header}`}>
                      {Icon && <Icon fontSize='small' />}
                      <span className='text-sm font-semibold uppercase tracking-wider'>
                        {type.label}
                      </span>
                      <span
                        className={`ml-auto rounded-full px-2 py-0.5 text-xs font-semibold ${type.badge}`}>
                        {items.length}
                      </span>
                    </div>
                    <div className='flex flex-col gap-3'>
                      {items.length === 0 ? (
                        <p className='text-sm text-muted-foreground'>Sin recursos.</p>
                      ) : (
                        items.map(item => {
                          const displayName = item.name;
                          const thumbnail = item.thumbnail;
                          return (
                            <button
                              type='button'
                              key={`${type.key}-${item.key}`}
                              onClick={() => openResource(type, item)}
                              className={`flex w-full items-center gap-3 rounded-lg border px-3 py-3 text-left transition duration-150 hover:shadow-solid-5 ${type.card}`}>
                              <ResourceThumb thumbnail={thumbnail} Icon={Icon} tile={type.tile} />
                              <span className='min-w-0'>
                                <span className='block truncate text-sm font-medium text-primary'>
                                  {displayName}
                                </span>
                              </span>
                            </button>
                          );
                        })
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default LearningPathView;
